import cloneDeep from 'lodash/cloneDeep';

import {SectionItem} from 'common/components/items-menu/types';
import {ChangeEvent} from 'common/components/select/types';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {ActionType, trackLearningAction} from 'common/util/server-tracking';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {DisplayContext, FilterValue} from 'common/widgets/property-filter-menu/models/types';
import {isLookupPropertyFilter, PropertyFilterFactory} from 'common/widgets/property-filter-menu/models/utils';
import {generateFilterTrackingProps, transformFilterToPropertyItem} from 'common/widgets/query-block/filter-menu/util';
import {
  Action,
  EntryPart,
  FiltersOperator as EntrySectionFilterOperator,
  QueryFilterEntry,
  SelectorTypeDefinitions,
} from 'common/widgets/query-entry-section/types';
import {PropertyType, ResourceType} from 'common/report/constants';
import {DEFAULT_FILTER_OPERATOR} from 'common/widgets/property-filter-menu/models/property-filter';

import {BaseEntryRow} from '../base';
import {EntryRowProps} from '../base/types';
import {FilterEntryRowEl} from './template';
import {SelectedOperator} from './constants';
import {applyFilterOperatorToFilter} from './utils';

export class FilterEntryRow extends BaseEntryRow<QueryFilterEntry> {
  filter: PropertyFilter;
  template = FilterEntryRowEl;

  constructor(props: EntryRowProps<QueryFilterEntry>) {
    super(props);
    this.filter = this.createFilter(props.entry.event);
  }

  defaultActiveEntryPart() {
    if (this.filter.hasSelectedProperty()) {
      return EntryPart.FilterValue;
    } else return EntryPart.Value;
  }

  /**
   * Generates a new empty QueryFilterEntry
   */
  static getNewEntry(): QueryFilterEntry {
    return {
      type: SelectorTypeDefinitions.Filter,
      filtersOperator: null,
      filters: [],
      event: null,
    };
  }

  createFilter(attrs: Partial<PropertyFilter>) {
    return PropertyFilterFactory.createPropertyFilter(attrs, {
      displayContext: DisplayContext.Default,
    });
  }

  isPlaceholder() {
    return !this.filter.isValid();
  }

  handlePropertyItemSelected(
    ev: CustomEvent<{item: PropertyFilter; trackingProps: Record<string, any>; queryReason: QueryReason}>,
  ) {
    const {item, trackingProps, queryReason} = ev.detail;

    // list quantifier UI for list properties
    if (
      item.propertyType === PropertyType.List &&
      item.resourceType !== ResourceType.Cohort &&
      !isLookupPropertyFilter(item)
    ) {
      item.joinPropertyType = PropertyType.List;
      item.propertyType = PropertyType.String;
      item.propertyDefaultType = PropertyType.String;
      item.filterOperator = DEFAULT_FILTER_OPERATOR[PropertyType.String];
    }

    // persist the selected filter operator and filter value if changing between same property types
    if (item.resourceType !== ResourceType.Cohort && this.filter.propertyType === item.propertyType) {
      item.filterValue = this.filter.filterValue;
      item.filterOperator = this.filter.filterOperator;
    }

    this.updateEntry(item, {trackingProps, queryReason});
  }

  setFilterAttribute(attribute: keyof PropertyFilter, value) {
    this.updateEntry(this.filter.setAttribute(attribute, value));
  }

  handleFilterValueChange(value: FilterValue) {
    this.setFilterAttribute(`filterValue`, value);
  }

  /**
   * Updates the selected filter operator and attemps to preserve
   * any pre-existing selected filter values
   */
  handleFilterOperatorChange(ev: ChangeEvent<SectionItem<SelectedOperator>>) {
    const [filterOperator] = ev.detail.item.value;
    const filter = applyFilterOperatorToFilter(filterOperator, this.filter);

    // focus on filter value when operator changes filter to an invalid state
    !filter.isValid() && this.section.setActiveEntry(this.index, EntryPart.FilterValue);

    this.updateEntry(filter);
  }

  getSelectedProperty() {
    return transformFilterToPropertyItem(this.filter);
  }

  handleEntryFilterOperatorChange(filtersOperator: EntrySectionFilterOperator) {
    const updatedEntry = {
      ...this.entry,
      filtersOperator,
    };
    this.section.updateEntries(Action.Edit, {entry: updatedEntry, index: this.index});
  }

  updateEntry(
    filter: PropertyFilter,
    {trackingProps, queryReason}: Partial<{trackingProps: Obj; queryReason: QueryReason}> = {},
  ) {
    const updatedEntry = {
      ...this.entry,
      event: filter instanceof PropertyFilter ? filter.getAttributes() : filter,
    };

    // when changing a committed filter to an invalid filter, or when the filter property
    // is selected for the first time, set focus to the filter value selection
    const isStagingChanges = this.section.isStagingEntryChanges(this.entry, updatedEntry);
    const hasProperty = this.filter.hasSelectedProperty();
    if (isStagingChanges || !hasProperty) {
      this.section.setActiveEntry(this.index, EntryPart.FilterValue);
    }

    // The filter-menu will pass through queryReason in which case we should use that queryReason.
    queryReason = queryReason || QueryReason.QbFilterUpdate;
    const updateStatus = this.section.updateEntries(Action.Edit, {
      entry: updatedEntry,
      index: this.index,
      trackingProps: trackingProps ?? generateFilterTrackingProps(filter),
      queryReason,
    });

    if (queryReason !== QueryReason.QbAutoTriggered && updateStatus.dispatchedUpdate) {
      // We don't want to track learning action until the complete filter has been created
      trackLearningAction({
        projectId: this.section.attr(`project-id`),
        reportName: this.section.attr(`track-name`),
        actionType: ActionType.Filter,
        resourceType: filter.resourceType,
      });
    }
  }

  getPropertyType(): PropertyType {
    return this.entry.event.propertyType;
  }

  setPropertyType(toType: PropertyType) {
    if (this.entry.event.propertyType === toType && this.entry.event.joinPropertyType !== PropertyType.List) {
      return;
    }

    const entry = cloneDeep(this.entry); // unfortunately we can't use immer's produce function because we mutate this object down the call chain
    const event = {
      ...entry.event,
      propertyType: toType,
      listQuantifier: null,
      joinPropertyType: null,
      filterOperator: null, // PropertyFilterFactory sets default values when filterOperator is falsey
      filterValue: null,
    };

    if (event.propertyType === PropertyType.List) {
      event.propertyType = PropertyType.String;
      event.joinPropertyType = PropertyType.List;
      event.filterOperator = DEFAULT_FILTER_OPERATOR[PropertyType.String];
    }

    entry.event = PropertyFilterFactory.createPropertyFilter(event, {
      displayContext: DisplayContext.Default,
    });

    this.section.updateEntries(Action.Edit, {entry, index: this.index});
  }
}
